import { useGLTF } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { useMemo, useRef } from 'react'
import * as THREE from 'three'
import type { GameRuntime } from '../game/runtime'
import { WINDOW_GUNNER_MODEL_URL } from './scene-contract'
import { getWindowGunnerFramePose, WINDOW_GUNNER_LAYOUT } from './window-gunner-contract'
import { applyWindowGunnerFrame } from './window-gunner-frame'

interface WindowGunnerProps {
  runtime: GameRuntime
  vehicleId: string
  accent: string
}

interface PreparedGunner {
  scene: THREE.Group
  scale: number
}

function prepareGunner(source: THREE.Group): PreparedGunner {
  const scene = source.clone(true)
  const bounds = new THREE.Box3().setFromObject(scene)
  const center = bounds.getCenter(new THREE.Vector3())
  const size = bounds.getSize(new THREE.Vector3())
  scene.position.sub(center)
  scene.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return
    object.castShadow = true
    object.receiveShadow = true
    const materials = Array.isArray(object.material) ? object.material : [object.material]
    const tuned = materials.map((material) => {
      const clone = material.clone()
      if (clone instanceof THREE.MeshStandardMaterial) {
        clone.envMapIntensity = 1.25
        clone.roughness = Math.max(0.2, clone.roughness)
        clone.color.lerp(new THREE.Color('#d8e2e8'), 0.16)
      }
      return clone
    })
    object.material = Array.isArray(object.material) ? tuned : (tuned[0] ?? object.material)
  })
  return { scene, scale: 1.45 / Math.max(0.001, size.x) }
}

export function WindowGunner({ runtime, vehicleId, accent }: WindowGunnerProps) {
  const rigRef = useRef<THREE.Group>(null)
  const recoilRef = useRef<THREE.Group>(null)
  const slideRef = useRef<THREE.Mesh>(null)
  const flashRef = useRef<THREE.Group>(null)
  const lightRef = useRef<THREE.PointLight>(null)
  const { scene } = useGLTF(WINDOW_GUNNER_MODEL_URL)
  const prepared = useMemo(() => prepareGunner(scene), [scene])

  useFrame((_, delta) => {
    const rig = rigRef.current
    const recoil = recoilRef.current
    const slide = slideRef.current
    const flash = flashRef.current
    const light = lightRef.current
    const state = runtime.getState()
    const vehicle = state.vehicles.find((candidate) => candidate.id === vehicleId)
    if (!rig || !recoil || !slide || !flash || !light) return

    const pose = getWindowGunnerFramePose({
      vehicle,
      paused: runtime.isPaused(),
      phase: state.race.phase,
    })
    applyWindowGunnerFrame({ root: rig, recoil, slide, flash, light }, pose, delta, state.tick)
  })

  return (
    <>
      <group ref={rigRef} visible={false} userData={{ testId: 'window-gunner', vehicleId }}>
        <group ref={recoilRef} position={WINDOW_GUNNER_LAYOUT.recoilPivot}>
          <group position={WINDOW_GUNNER_LAYOUT.gunner} rotation={[0, -Math.PI / 2, 0]}>
            <primitive object={prepared.scene} scale={prepared.scale} />
          </group>

          <mesh ref={slideRef} position={WINDOW_GUNNER_LAYOUT.slide} castShadow>
            <boxGeometry args={[0.13, 0.1, 0.38]} />
            <meshStandardMaterial color="#10151b" roughness={0.18} metalness={0.82} />
          </mesh>
          <mesh position={WINDOW_GUNNER_LAYOUT.barrel} rotation={[Math.PI / 2, 0, 0]} castShadow>
            <cylinderGeometry args={[0.032, 0.037, 0.15, 12]} />
            <meshStandardMaterial color="#090c10" metalness={0.9} roughness={0.16} />
          </mesh>
          <mesh position={WINDOW_GUNNER_LAYOUT.sight} castShadow>
            <boxGeometry args={[0.035, 0.035, 0.12]} />
            <meshStandardMaterial
              color="#78f7ff"
              emissive={accent}
              emissiveIntensity={0.55}
              metalness={0.65}
              roughness={0.22}
            />
          </mesh>
        </group>

        <group ref={flashRef} position={WINDOW_GUNNER_LAYOUT.flash} visible={false}>
          <mesh position={[0, 0, 0.2]} rotation={[Math.PI / 2, 0, 0]}>
            <coneGeometry args={[0.13, 0.48, 5]} />
            <meshBasicMaterial
              color="#fff3bd"
              toneMapped={false}
              transparent
              opacity={0.92}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
          <mesh scale={[0.12, 0.12, 0.22]}>
            <octahedronGeometry args={[1, 0]} />
            <meshBasicMaterial
              color="#ff7a24"
              toneMapped={false}
              transparent
              opacity={0.88}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
        </group>
      </group>
      <pointLight
        ref={lightRef}
        position={WINDOW_GUNNER_LAYOUT.flash}
        color="#ff8a3c"
        intensity={0}
        distance={8}
        decay={2}
      />
    </>
  )
}

useGLTF.preload(WINDOW_GUNNER_MODEL_URL)
